import React, { useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { ArrowLeftRight, CalendarDays, Plane, Users } from 'lucide-react';

const TRAVEL_TABS = [
  'Flight',
  'Hotel',
  'Shop',
  'Holiday',
  'Visa',
  'Medical',
  'Cars',
  'eSIM',
  'Recharge',
  'Pay Bill',
];

const TRIP_TYPES = ['One Way', 'Round Trip', 'Multi City'];
const FARE_TYPES = ['Regular Fare', 'Student Fare', 'Umrah Fare'];
const TRAVELLER_OPTIONS = [
  '1 Traveller',
  '2 Travellers',
  '3 Travellers',
  '4 Travellers',
];
const CLASS_OPTIONS = ['Economy', 'Premium Economy', 'Business'];

const CITY_OPTIONS = [
  { value: 'Dhaka', code: 'DAC', country: 'Bangladesh' },
  { value: 'Chittagong', code: 'CGP', country: 'Bangladesh' },
  { value: 'Sylhet', code: 'ZYL', country: 'Bangladesh' },
  { value: 'Toronto', code: 'YYZ', country: 'Canada' },
  { value: 'Vancouver', code: 'YVR', country: 'Canada' },
  { value: 'London', code: 'LHR', country: 'United Kingdom' },
  { value: 'Manchester', code: 'MAN', country: 'United Kingdom' },
  { value: 'Sydney', code: 'SYD', country: 'Australia' },
  { value: 'Melbourne', code: 'MEL', country: 'Australia' },
  { value: 'New York', code: 'JFK', country: 'United States' },
];

function TravelTab({ active, children }) {
  return (
    <button
      type="button"
      className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'bg-orange-100 text-orange-600'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  );
}

function FilterPill({ active, children, onClick, tone = 'blue' }) {
  const activeClass =
    tone === 'orange'
      ? 'bg-orange-500 text-white shadow-sm'
      : 'bg-blue-600 text-white shadow-sm';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${
        active
          ? activeClass
          : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
        {title}
      </div>
      {children}
    </div>
  );
}

function AirportField({ title, option, value, onChange }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
      <div className="flex min-w-0 flex-col">
        <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
          {title}
        </span>
        <Input
          list={`${title.toLowerCase()}-city-options`}
          value={value}
          onChange={event => onChange(event.target.value)}
          placeholder="Type or choose a city"
          className="mt-1 h-auto border-0 bg-transparent px-0 py-0 text-sm font-semibold text-slate-900 shadow-none focus-visible:ring-0"
        />
        <datalist id={`${title.toLowerCase()}-city-options`}>
          {CITY_OPTIONS.map(city => (
            <option key={city.value} value={city.value}>
              {city.code} - {city.value}, {city.country}
            </option>
          ))}
        </datalist>
        {option && (
          <div className="mt-1 truncate text-xs text-slate-500">
            {option.code} - {option.value}, {option.country}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TicketTravelBookingCard({
  applying,
  onApply,
  service,
}) {
  const [tripType, setTripType] = useState('Round Trip');
  const [fareType, setFareType] = useState('Regular Fare');
  const [fromCity, setFromCity] = useState('Dhaka');
  const [toCity, setToCity] = useState('Toronto');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [travellerCount, setTravellerCount] = useState('1 Traveller');
  const [travelClass, setTravelClass] = useState('Economy');

  const fromOption = useMemo(
    () => CITY_OPTIONS.find(option => option.value === fromCity),
    [fromCity],
  );
  const toOption = useMemo(
    () => CITY_OPTIONS.find(option => option.value === toCity),
    [toCity],
  );

  const handleSwap = () => {
    setFromCity(toCity);
    setToCity(fromCity);
  };

  const handleApplyClick = () => {
    onApply({
      formData: {
        destinationCountry: toOption?.country || '',
        destinationCity: toCity,
        departureCity: fromCity,
        preferredDepartureDate: departureDate,
        budgetRange: '',
        travelInsuranceNeeded: false,
        airportPickupNeeded: false,
      },
      uiState: {
        returnDate,
      },
    });
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-6 py-6">
      <div className="overflow-hidden rounded-[28px] border border-[#e2e7d8] bg-[#F0F2EC] shadow-sm">
        <div className="px-6 py-6 md:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            Ticket & Travel Support
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600 md:text-base">
            {service?.tagline ||
              'Plan your route, set travel preferences, and start your Fly8 support request through a booking-style experience.'}
          </p>
        </div>

        <div className="px-6 pb-6 md:px-8">
          <div className="w-full rounded-[26px] border border-gray-200 bg-gradient-to-br from-[#F8FAF5] to-[#EEF2E6] p-4 shadow-sm md:p-6">
            <div className="space-y-4">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap gap-2">
                  {TRIP_TYPES.map(type => (
                    <FilterPill
                      key={type}
                      active={tripType === type}
                      onClick={() => setTripType(type)}
                    >
                      {type}
                    </FilterPill>
                  ))}
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:w-[360px]">
                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-2.5">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Travellers
                    </div>
                    <Select
                      value={travellerCount}
                      onValueChange={setTravellerCount}
                    >
                      <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-sm font-semibold text-slate-900 shadow-none focus:ring-0">
                        <span className="inline-flex items-center gap-2">
                          <Users className="h-4 w-4 text-slate-500" />
                          <SelectValue placeholder="Select travellers" />
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {TRAVELLER_OPTIONS.map(option => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white px-4 py-2.5">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Class
                    </div>
                    <Select value={travelClass} onValueChange={setTravelClass}>
                      <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-sm font-semibold text-slate-900 shadow-none focus:ring-0">
                        <SelectValue placeholder="Select class" />
                      </SelectTrigger>
                      <SelectContent>
                        {CLASS_OPTIONS.map(option => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 xl:grid-cols-12 xl:items-center">
                <div className="xl:col-span-5">
                  <AirportField
                    title="FROM"
                    option={fromOption}
                    value={fromCity}
                    onChange={setFromCity}
                  />
                </div>

                <div className="flex justify-center xl:col-span-1">
                  <button
                    type="button"
                    onClick={handleSwap}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-slate-500 shadow-sm transition-all hover:border-orange-200 hover:text-orange-500"
                    aria-label="Swap locations"
                  >
                    <ArrowLeftRight className="h-5 w-5" />
                  </button>
                </div>

                <div className="xl:col-span-5">
                  <AirportField
                    title="TO"
                    option={toOption}
                    value={toCity}
                    onChange={setToCity}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12 xl:items-stretch">
                <div className="xl:col-span-3">
                  <div className="h-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Departure Date
                    </div>
                    <div className="flex items-center gap-2 text-slate-900">
                      <CalendarDays className="h-4 w-4 text-slate-500" />
                      <Input
                        type="date"
                        value={departureDate}
                        onChange={event => setDepartureDate(event.target.value)}
                        className="h-auto rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-none focus-visible:ring-0"
                      />
                    </div>
                  </div>
                </div>

                <div className="xl:col-span-3">
                  <div className="h-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                      Return Date
                    </div>
                    <div className="flex items-center gap-2 text-slate-900">
                      <CalendarDays className="h-4 w-4 text-slate-500" />
                      <Input
                        type="date"
                        value={returnDate}
                        onChange={event => setReturnDate(event.target.value)}
                        disabled={tripType === 'One Way'}
                        className="h-auto rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                <div className="xl:col-span-4">
                  <Button
                    onClick={handleApplyClick}
                    disabled={applying}
                    className="h-full min-h-[56px] w-full rounded-xl bg-orange-500 px-5 py-2.5 text-base font-semibold text-white shadow-sm transition-all hover:scale-[1.02] hover:bg-orange-600"
                  >
                    <span className="inline-flex items-center gap-2">
                      {/* <Plane className="h-5 w-5" /> */}
                      {applying
                        ? 'Opening application...'
                        : 'Apply for Ticket & Travel Support'}
                    </span>
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {FARE_TYPES.map(type => (
                  <FilterPill
                    key={type}
                    tone="orange"
                    active={fareType === type}
                    onClick={() => setFareType(type)}
                  >
                    {type}
                  </FilterPill>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <InfoCard title="Destination Country">
                  <p className="text-sm font-semibold text-slate-900">
                    {toOption?.country || 'Select destination'}
                  </p>
                </InfoCard>

                <InfoCard title="Travel Setup">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                      {travellerCount}
                    </span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                      {travelClass}
                    </span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
                      {fareType}
                    </span>
                  </div>
                </InfoCard>

                <InfoCard title="Route Preview">
                  <p className="text-sm font-semibold text-slate-900">
                    {fromOption?.code || '---'} to {toOption?.code || '---'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    {departureDate || 'Choose your departure date'}
                  </p>
                </InfoCard>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
